import { gsap } from 'gsap'
import type { Tween } from 'gsap/Tween'
import type { NodesData } from '../types'
import { Event } from '../types'
import { EventBus } from './event-bus'
import { EntityRegistry } from './entity-registry'

export class AnimationManager {
  private static instance: AnimationManager | null = null
  private readonly eventBus: EventBus = EventBus.getInstance()
  private readonly registry: EntityRegistry = EntityRegistry.getInstance()
  private readonly tweens: Map<string, Tween> = new Map()
  private readonly visibleNodes: Set<string> = new Set()

  private constructor () {
    this.initEventListeners()
  }

  public static getInstance (): AnimationManager {
    if (AnimationManager.instance === null) {
      AnimationManager.instance = new AnimationManager()
    }
    return AnimationManager.instance
  }

  private initEventListeners (): void {
    this.eventBus.subscribe(Event.SLICE_HOVERED, (data) => {
      this.highlightSlice(data.pieId, data.sliceId)
    })

    this.eventBus.subscribe(Event.SLICE_UNHOVERED, (data) => {
      this.unhighlightSlice(data.pieId, data.sliceId)
    })

    this.eventBus.subscribe(Event.SLICE_CLICKED, (data) => {
      this.animateSliceScale(data.pieId, data.sliceId)
      this.highlightSlice(data.pieId, data.sliceId)
    })

    this.eventBus.subscribe(Event.HIDE_NODES, (data: NodesData) => {
      data.nodes.map(n => n.id).reverse().forEach((id, index) =>
        this.animateNodeVisibility(id, false, index * 0.05)
      )
    })

    this.eventBus.subscribe(Event.SHOW_NODES, (data: NodesData) => {
      data.nodes.map(n => n.id).forEach((id, index) =>
        this.animateNodeVisibility(id, true, index * 0.05)
      )
    })
  }

  private highlightSlice (pieId: string, sliceId: string): void {
    const slice = this.registry.getPieSlice(pieId, sliceId)
    if (slice === null) return

    slice.tint = 0xFFFFFF
    this.eventBus.publish(Event.REDRAW)
  }

  private unhighlightSlice (pieId: string, sliceId: string): void {
    const slice = this.registry.getPieSlice(pieId, sliceId)
    if (slice === null) return

    slice.tint = 0x333333
    this.eventBus.publish(Event.REDRAW)
  }

  private animateSliceScale (pieId: string, sliceId: string): void {
    const slice = this.registry.getPieSlice(pieId, sliceId)
    if (slice === null) return

    const tweenId = `scale_${pieId}_${sliceId}`
    this.stopTween(tweenId)

    const endScale = slice.scale.x === 1.05 ? 1.0 : 1.05

    const tween = gsap.to(slice.scale, {
      x: endScale,
      y: endScale,
      duration: 0.1,
      ease: 'power2.out',
      onUpdate: () => this.eventBus.publish(Event.REDRAW),
      onComplete: () => this.completeTween(tweenId)
    })

    this.tweens.set(tweenId, tween)
  }

  private animateNodeVisibility (nodeId: string, visible: boolean, delay = 0): void {
    const node = this.registry.getNode(nodeId)
    if (node === null) return

    const tweenId = `visibility_${nodeId}`
    this.stopTween(tweenId)

    const endScale = visible ? 1 : 0

    const tween = gsap.to(node.scale, {
      x: endScale,
      y: endScale,
      duration: 0.3,
      ease: 'power2.inOut',
      delay,
      onUpdate: () => this.eventBus.publish(Event.REDRAW),
      onComplete: () => {
        this.updateVisibleNodes(nodeId, visible)
        this.completeTween(tweenId)
      }
    })

    this.tweens.set(tweenId, tween)
  }

  private updateVisibleNodes (nodeId: string, visible: boolean): void {
    if (visible) {
      this.visibleNodes.add(nodeId)
    } else {
      this.visibleNodes.delete(nodeId)
    }
  }

  private completeTween (id: string): void {
    this.eventBus.publish(Event.ANIMATION_COMPLETE, { id })
    this.tweens.delete(id)
  }

  private stopTween (id: string): void {
    const tween = this.tweens.get(id)
    if (tween != null) {
      tween.kill()
      this.tweens.delete(id)
    }
  }

  private tweenIds (): string[] {
    return Array.from(this.tweens.keys())
  }

  public stopAllAnimations (): void {
    this.tweenIds().forEach(id => {
      this.stopTween(id)
    })
  }
}
